import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { ProposalEntity } from 'v2/models';
import DataRequestItem from '../DataRequestItem/DataRequestItem';
import styles from './activeDataRequests.module.scss';

export type ActiveRequestsTabType = {
  eventKey: 'waiting-seller' | 'waiting-buyer' | 'approved';
  title: string;
  list: ProposalEntity[];
};

export interface ActiveDataRequestsOwnProps {
  waitingSellerProposals: ProposalEntity[];
  waitingBuyerProposals: ProposalEntity[];
  approvedProposals: ProposalEntity[];
}

export type ActiveDataRequestsProps = ActiveDataRequestsOwnProps;

const ActiveDataRequests = ({
  waitingSellerProposals,
  waitingBuyerProposals,
  approvedProposals,
}: ActiveDataRequestsProps) => {
  const TABS: ActiveRequestsTabType[] = [
    {
      eventKey: 'waiting-seller',
      title: `waiting for you`,
      list: waitingSellerProposals,
    },
    {
      eventKey: 'waiting-buyer',
      title: `waiting for buyer`,
      list: waitingBuyerProposals,
    },
    {
      eventKey: 'approved',
      title: `approved`,
      list: approvedProposals,
    },
  ];
  const { path } = useRouteMatch();
  const { pathname } = useLocation();
  const [selectedTabUrl, setSelectedTabUrl] = useState(
    pathname.split('/')?.[3]
  );
  const history = useHistory();
  const defaultTab = TABS[0].eventKey;
  const [key, setKey] = useState<string>(selectedTabUrl || defaultTab);

  useEffect(() => {
    setSelectedTabUrl(pathname);

    const newKey = pathname.split('/')?.[3];
    if (newKey && newKey !== key) {
      setKey(newKey);
    }
  }, [pathname, key]);

  if (!selectedTabUrl) {
    return <Redirect to={`${path}/${defaultTab}`} />;
  }

  return (
    <div
      className={classnames('container-xl', 'fix-bg-images', styles.root)}
      data-cy="proposals-list"
    >
      <Tabs
        className={classnames('border-bottom-0', styles.scrolling_tabs)}
        activeKey={key}
        onSelect={(key) => {
          setKey(key || defaultTab);
          history.push(`${path}/${key}`);
        }}
        role="tab"
        transition={false}
      >
        {TABS.map((item) => (
          <Tab
            key={item.eventKey}
            className="border-primary"
            eventKey={item.eventKey}
            unmountOnExit={true}
            title={`${item.title} (${item.list.length})`}
          >
            <div className={classnames('pt-3 h-100 position-relative')}>
              <Switch>
                <Route path={`${path}/waiting-seller`}>
                  <h5
                    className={classnames(
                      'text-primary-dark mt-4 mt-lg-5 mb-4',
                      styles.description
                    )}
                  >
                    {waitingSellerProposals?.length
                      ? 'Requests below are new and awaiting your review.'
                      : 'You have no active requests at this time.'}
                  </h5>
                  {item.list.map((proposal) => (
                    <DataRequestItem
                      key={proposal.id}
                      tabKey={'waiting-seller'}
                      className="mb-3"
                      proposal={proposal}
                    />
                  ))}
                </Route>
                <Route path={`${path}/waiting-buyer`}>
                  <h5
                    className={classnames(
                      'text-primary-dark mt-5 mb-4',
                      styles.description
                    )}
                  >
                    {waitingBuyerProposals?.length
                      ? 'Requests below are waiting for buyer review.'
                      : 'You have no requests awaiting a buyer at this time.'}
                  </h5>
                  {item.list.map((proposal) => (
                    <DataRequestItem
                      key={proposal.id}
                      tabKey={'waiting-buyer'}
                      className="mb-3"
                      proposal={proposal}
                    />
                  ))}
                </Route>
                <Route path={`${path}/approved`}>
                  <h5
                    className={classnames(
                      'text-primary-dark mt-5 mb-4',
                      styles.description
                    )}
                  >
                    {approvedProposals?.length
                      ? 'Requests below are approved by the buyer and are currently in discussion with that buyer.'
                      : 'You have no approved requests at this time.'}
                  </h5>
                  {item.list.map((proposal) => (
                    <DataRequestItem
                      key={proposal.id}
                      tabKey={'approved'}
                      className="mb-3"
                      proposal={proposal}
                    />
                  ))}
                </Route>
              </Switch>
            </div>
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};

export default ActiveDataRequests;
